import compact from 'lodash/compact'
import every from 'lodash/every'
import sortBy from 'lodash/sortBy'
import {fromJust} from '@freckle/maybe'
import {capitalizeFirstLetter} from '@freckle/educator-entities/ts/common/helpers/string-helper'
import type {RlStandardSetT} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import {formattedName} from '@freckle/educator-entities/ts/users/models/student'
import {OverlayTrigger} from '@freckle/educator-materials/ts/components/overlay-trigger'
import {Popover} from '@freckle/educator-materials/ts/components/popover'
import {StudentGroupPanel} from '@freckle/educator-materials/ts/components/student-group-panel'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import type {
  ProficiencyReportTableRowT,
  StudentCategoriesT,
} from '@freckle/school/ts/proficiency-report/logic/proficiency-report-table-logic'
import {StudentCategories} from '@freckle/school/ts/proficiency-report/logic/proficiency-report-table-logic'
import type {ProficiencyReportStudentDataT} from '@freckle/school/ts/proficiency-report/models/proficiency-report'
import type {StudentsMapT} from '@freckle/school/ts/users/collections/students'

type Props = {
  students: StudentsMapT
  bucketData: ProficiencyReportTableRowT
  category: StudentCategoriesT
  mathStandardSet: RlStandardSetT
}

export function ProficiencyReportStudentBucket(props: Props) {
  const {t} = useSafeTranslation()

  const insufficientDataPopover = (
    <Popover id="insufficient-data-popover">
      <p>{t('PROFICIENCY_REPORT_STUDENT_BUCKET_INSUFFICIENT_DATA')}</p>
    </Popover>
  )

  const panelHeader =
    props.category === StudentCategories.Insufficient ? (
      <div>
        <span>{StudentCategories.forDisplay(props.category)}</span>&nbsp;
        <OverlayTrigger
          trigger={['hover', 'focus']}
          placement="bottom"
          overlay={insufficientDataPopover}
        >
          <i className="fa fa-question-circle hidden-print" />
        </OverlayTrigger>
      </div>
    ) : (
      <span>{StudentCategories.forDisplay(props.category)}</span>
    )

  const [panelClass, studentData] = (() => {
    switch (props.category) {
      case StudentCategories.Insufficient:
        return ['bucket-default', props.bucketData.studentGroups.insufficientData]
      case StudentCategories.Below:
        return ['bucket-danger', props.bucketData.studentGroups.belowGradeLevel]
      case StudentCategories.Within:
        return ['bucket-warning', props.bucketData.studentGroups.withinGradeLevel]
      case StudentCategories.Above:
        return ['bucket-success', props.bucketData.studentGroups.aboveGradeLevel]
      default:
        throw new Error(`${props.category} is not a student category`)
    }
  })()

  const shouldShowStandardCode = props.category !== StudentCategories.Insufficient

  const tableHeader = (
    <tr>
      <th className="col-8 student-name">{t('PROFICIENCY_REPORT_STUDENT_COLUMN_HEADER_NAME')}</th>
      {shouldShowStandardCode ? (
        <th className="col-4 student-standard-code">
          {capitalizeFirstLetter(props.mathStandardSet.standardLabel)}
        </th>
      ) : null}
    </tr>
  )

  const sortedStudentData = sortStudentGroupByName(studentData, props.students)

  const body = compact(
    sortStudentGroupByStandardCode(sortedStudentData).map((student, i: number) => {
      const studentModel = fromJust(
        props.students.get(student.id),
        `Could not find student: ${student.id}`,
      )
      return (
        <tr key={`student-bucket-row-${student.id}-${i}`} className="student-table-body">
          <td data-pii className="student-name">
            {formattedName(studentModel)}
          </td>
          {shouldShowStandardCode && student.standardName !== null ? (
            <td className="student-standard-code">
              <span>{student.standardName}</span>
            </td>
          ) : null}
        </tr>
      )
    }),
  )

  return (
    <div className="bucket col-3">
      <StudentGroupPanel panelStyle="default" panelClass={panelClass} header={panelHeader}>
        <table>
          <thead>{tableHeader}</thead>
          <tbody>{body}</tbody>
        </table>
      </StudentGroupPanel>
    </div>
  )
}

function sortStudentGroupByStandardCode(
  studentGroup: Array<ProficiencyReportStudentDataT>,
): Array<ProficiencyReportStudentDataT> {
  return every(studentGroup, std => std.standardName !== null && std.standardName !== undefined)
    ? [...studentGroup].sort(compareStudentData)
    : studentGroup
}

function sortStudentGroupByName(
  studentGroup: Array<ProficiencyReportStudentDataT>,
  students: StudentsMapT,
): Array<ProficiencyReportStudentDataT> {
  return sortBy(
    studentGroup.filter(student => students.get(student.id) !== undefined),
    student => {
      return students.get(student.id)?.lastName
    },
  )
}

function compareStudentData(
  a: ProficiencyReportStudentDataT,
  b: ProficiencyReportStudentDataT,
): number {
  const standardCodeA = fromJust(
    a.standardName,
    `standardName undefined or null for student: ${a.id}`,
  )
  const standardCodeB = fromJust(
    b.standardName,
    `standardName undefined or null for student: ${b.id}`,
  )

  return standardCodeA.localeCompare(standardCodeB)
}
